import { useState, useEffect, useCallback } from 'react'
import type { Category, FamilyMember } from '@/types/database'

interface TransactionDisplay {
  id: number;
  type: "income" | "expense";
  amount: number;
  date: string;
  description: string | null;
  is_recurring: boolean;
  category: {
    id: number;
    name: string;
    icon: string | null;
    color: string;
  };
  family_member: {
    id: number;
    name: string;
    color: string;
  };
}

interface TransactionListProps {
  type?: "income" | "expense";
  limit?: number;
  showFilters?: boolean;
  refreshTrigger?: number;
}

export function TransactionList({
  type,
  limit = 20,
  showFilters = true,
  refreshTrigger = 0,
}: TransactionListProps) {
  const [transactions, setTransactions] = useState<TransactionDisplay[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  // Filters
  const [categoryFilter, setCategoryFilter] = useState("");
  const [familyMemberFilter, setFamilyMemberFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchTransactions = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const params = new URLSearchParams();
      if (type) params.set("type", type);
      if (categoryFilter) params.set("category_id", categoryFilter);
      if (familyMemberFilter) params.set("family_member_id", familyMemberFilter);
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      params.set("limit", limit.toString());

      const response = await fetch(`/api/transactions?${params.toString()}`);
      if (!response.ok) throw new Error("Failed to fetch transactions");

      const data = (await response.json()) as {
        transactions: TransactionDisplay[];
      };
      setTransactions(data.transactions || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  }, [type, categoryFilter, familyMemberFilter, startDate, endDate, limit]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions, refreshTrigger]);

  useEffect(() => {
    // Fetch filter options
    async function fetchFilterOptions() {
      try {
        const [categoriesRes, familyMembersRes] = await Promise.all([
          fetch(`/api/categories${type ? `?type=${type}` : ""}`),
          fetch("/api/family-members"),
        ]);

        const categoriesData = (await categoriesRes.json()) as {
          categories: Category[];
        };
        const familyMembersData = (await familyMembersRes.json()) as {
          familyMembers: FamilyMember[];
        };

        setCategories(categoriesData.categories || []);
        setFamilyMembers(familyMembersData.familyMembers || []);
      } catch (err) {
        console.error("Error fetching filter options:", err);
      }
    }

    fetchFilterOptions();
  }, [type]);

  async function handleDelete(id: number) {
    if (!confirm("Are you sure you want to delete this transaction?")) return;

    try {
      const response = await fetch(`/api/transactions/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) throw new Error("Failed to delete");

      setTransactions((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : "Failed to delete");
    }
  }

  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  }

  function formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  if (error) {
    return (
      <div className="text-center py-8 text-danger">
        {error}
        <button
          onClick={fetchTransactions}
          className="ml-2 underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      {showFilters && (
        <div className="flex flex-wrap gap-3">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-stone/30 focus:border-sage outline-none"
          >
            <option value="">All Categories</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.icon} {cat.name}
              </option>
            ))}
          </select>

          <select
            value={familyMemberFilter}
            onChange={(e) => setFamilyMemberFilter(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-stone/30 focus:border-sage outline-none"
          >
            <option value="">All Members</option>
            {familyMembers.map((fm) => (
              <option key={fm.id} value={fm.id}>
                {fm.name}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-stone/30 focus:border-sage outline-none"
            placeholder="Start date"
          />

          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-3 py-1.5 text-sm rounded-lg border border-stone/30 focus:border-sage outline-none"
            placeholder="End date"
          />

          {(categoryFilter || familyMemberFilter || startDate || endDate) && (
            <button
              onClick={() => {
                setCategoryFilter("");
                setFamilyMemberFilter("");
                setStartDate("");
                setEndDate("");
              }}
              className="px-3 py-1.5 text-sm text-sage hover:text-charcoal"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-8 text-sage">Loading transactions...</div>
      )}

      {/* Empty State */}
      {!isLoading && transactions.length === 0 && (
        <div className="text-center py-8">
          <p className="text-sage">No transactions found</p>
        </div>
      )}

      {/* Transaction Table */}
      {!isLoading && transactions.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-sm text-sage border-b border-stone/20">
                <th className="pb-3 font-medium">Date</th>
                <th className="pb-3 font-medium">Description</th>
                <th className="pb-3 font-medium">Category</th>
                <th className="pb-3 font-medium">Person</th>
                <th className="pb-3 font-medium text-right">Amount</th>
                <th className="pb-3 font-medium w-10"></th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((transaction) => (
                <tr
                  key={transaction.id}
                  className="border-b border-stone/10 hover:bg-cream/50"
                >
                  <td className="py-3 text-sm text-charcoal">
                    {formatDate(transaction.date)}
                  </td>
                  <td className="py-3 text-sm text-charcoal">
                    {transaction.description || (
                      <span className="text-stone italic">No description</span>
                    )}
                    {transaction.is_recurring && (
                      <span
                        className="ml-2 text-xs bg-sage/10 text-sage px-1.5 py-0.5 rounded"
                        title="Recurring"
                      >
                        🔄
                      </span>
                    )}
                  </td>
                  <td className="py-3 text-sm">
                    <span
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: `${transaction.category.color}20`,
                        color: transaction.category.color,
                      }}
                    >
                      {transaction.category.icon} {transaction.category.name}
                    </span>
                  </td>
                  <td className="py-3 text-sm">
                    <span
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: `${transaction.family_member.color}20`,
                        color: transaction.family_member.color,
                      }}
                    >
                      {transaction.family_member.name}
                    </span>
                  </td>
                  <td
                    className={`py-3 text-sm font-medium text-right tabular-nums ${
                      transaction.type === "income"
                        ? "text-success"
                        : "text-danger"
                    }`}
                  >
                    {transaction.type === "income" ? "+" : "-"}
                    {formatCurrency(transaction.amount)}
                  </td>
                  <td className="py-3 text-right">
                    <button
                      onClick={() => handleDelete(transaction.id)}
                      className="text-stone hover:text-danger transition-colors text-sm"
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
